import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "433",
  "slug": "how-old-are-google-maps-satellite-images",
  "title": "How Old Are Google Maps Satellite Images?",
  "excerpt": "That satellite view of your house probably is not live, and it might not even be from this year. Here is how recent Google Maps imagery actually is, and why it varies so much from place to place.",
  "date": "2026-06-14",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1614730321146-b6fa6a46bcb4?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzYXRlbGxpdGUlMjBlYXJ0aCUyMHZpZXd8ZW58MXwwfHx8MTc4MTQyNTA3Nnww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Google Maps satellite imagery is not live. It is a stitched-together mosaic that is usually one to three years old, though busy cities are refreshed more often and remote areas can go many years between updates.",
  "content": [
    {
      "type": "paragraph",
      "text": "It is one of the most common misconceptions about online maps: the belief that the satellite view shows the world as it looks right now. People open Google Maps hoping to spot a car in their driveway or check whether it is snowing somewhere, and are surprised to find a sunny day from a season that ended long ago."
    },
    {
      "type": "paragraph",
      "text": "The truth is that satellite imagery on Google Maps is a snapshot of the past, not a live feed. And how far in the past depends entirely on where you are looking."
    },
    {
      "type": "heading",
      "text": "It is a mosaic, not a single photo"
    },
    {
      "type": "paragraph",
      "text": "The seamless satellite layer you scroll across is not one giant image. It is a mosaic stitched together from thousands of separate pictures, captured at different times by different sources, then color-corrected and blended so the seams mostly disappear. Some tiles come from satellites operated by commercial providers; others, especially the very sharp views of cities, come from aircraft flying planned photo missions."
    },
    {
      "type": "paragraph",
      "text": "Because the pieces are collected over time, two neighboring areas can be months or even years apart. That is why you sometimes see a sharp line where one image meets another, with different lighting or even a different season on each side."
    },
    {
      "type": "heading",
      "text": "So how old is it, really?"
    },
    {
      "type": "paragraph",
      "text": "There is no single answer, but a useful rule of thumb is one to three years for most populated places. The pattern looks roughly like this:"
    },
    {
      "type": "list",
      "items": [
        "Major cities and fast-changing suburbs are updated most often, sometimes within a year, because demand and ground-level changes are highest there.",
        "Smaller towns and rural areas are refreshed less frequently, often every few years.",
        "Remote regions, deserts, mountains, and open ocean can go many years between updates, simply because almost nothing changes and few people look."
      ]
    },
    {
      "type": "paragraph",
      "text": "If you want the exact date for a specific spot, open Google Earth rather than Google Maps. It displays the capture date of the imagery at the bottom of the screen, and even lets you scroll back through a timeline of older images of the same location, which is a fascinating way to watch a neighborhood, glacier, or coastline change over the years."
    },
    {
      "type": "heading",
      "text": "Why not just make it live?"
    },
    {
      "type": "paragraph",
      "text": "A truly live view of the entire planet at high resolution would require an enormous constellation of satellites and a staggering amount of data, and most of it would be hidden under clouds at any given moment. For everyday navigation, freshness simply is not worth that cost. Knowing where a building is matters far more than knowing what the weather was doing this morning."
    },
    {
      "type": "heading",
      "text": "Different layers age differently"
    },
    {
      "type": "paragraph",
      "text": "It also helps to remember that Google Maps has several visual layers, and they do not share an age. The flat satellite layer, the angled 3D buildings in some cities, and Street View are all captured separately, on different schedules. It is entirely normal for the overhead satellite image of a street to be two years old while the Street View photos along it are four years old, or the other way around. If one layer shows a building that the other does not, you are simply seeing two different moments in time stacked on the same map."
    },
    {
      "type": "paragraph",
      "text": "Seasonal mismatches add to the confusion. Because tiles are collected whenever conditions are clear, a single city can show green summer trees in one neighborhood and bare winter branches a few blocks over. None of this is a glitch; it is just the unavoidable result of building one smooth picture out of thousands of separate captures."
    },
    {
      "type": "heading",
      "text": "When fresher imagery matters"
    },
    {
      "type": "paragraph",
      "text": "For some uses, the age of the picture is the whole point. Scientists tracking deforestation, flooding, or crop health need imagery that is days old, not years. That is where satellites like the European Sentinel-2 program come in, photographing the same patch of Earth every few days at moderate resolution, trading some sharpness for speed."
    }
  ]
};
